import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/** Vista previa de la reunión: no manda nada hasta apretar «Enviar cobro». Un correo por
 * cliente con todas sus facturas vencidas juntas. */
export const CarteraPrepararDialog = ({ open, onOpenChange, customerIds, onDone }) => {
  const { t } = useT();
  const [needsForce, setNeedsForce] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["cartera-preparar", customerIds],
    queryFn: async () => (await api.post("/finance/cartera/preparar", { customer_ids: customerIds })).data,
    enabled: open && customerIds.length > 0,
  });

  useEffect(() => { if (!open) setNeedsForce(false); }, [open]);

  const enviar = useMutation({
    mutationFn: (forzar) => api.post("/finance/cartera/enviar", { customer_ids: customerIds, forzar }),
    onSuccess: ({ data: res }) => {
      toast.success(t("finance.cartera.sent", { n: res.length }));
      onOpenChange(false);
      onDone?.();
    },
    onError: (e) => {
      if (e?.response?.status === 409) setNeedsForce(true);
      else toast.error(apiError(e, t("app.error")));
    },
  });

  const previews = data || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cartera-preparar-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("finance.cartera.preview_title")}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-[13px] w-full bg-surface-sunken" />)}</div>
          ) : previews.map((p) => (
            <div key={p.customer_id} data-testid={`cartera-preview-${p.customer_id}`} className="border border-line p-3">
              <p className="text-[13px] font-semibold text-ink">{p.customer_nombre}</p>
              <p className="mt-1 text-[12px] text-ink-tertiary">
                {t("finance.cartera.recipient")}: <span className="text-ink">{p.destinatario || "—"}</span>
              </p>
              <p className="mt-1 text-[12px] text-ink-tertiary">
                {t("finance.cartera.subject")}: <span className="text-ink">{p.asunto}</span>
              </p>
              <div className="mt-2 max-h-32 overflow-y-auto whitespace-pre-wrap border-t border-line pt-2 text-[12px] text-ink-secondary">
                {p.cuerpo}
              </div>
              <p className="mt-2 text-[11.5px] text-ink-tertiary">
                {t("finance.cartera.invoices")}: {p.facturas.length} · {t("finance.cartera.attachments")}: {p.formato_adjuntos}
              </p>
            </div>
          ))}
        </div>
        {needsForce && (
          <p data-testid="cartera-force-confirm" className="flex items-center gap-1.5 border-l-2 border-state-warn bg-state-warn-soft px-2.5 py-1.5 text-[11.5px] font-medium text-state-warn">
            <AlertTriangle className="h-3.5 w-3.5" /> {t("finance.cartera.force_confirm")}
          </p>
        )}
        <DialogFooter>
          {needsForce ? (
            <Button data-testid="cartera-force-send" variant="outline" disabled={enviar.isPending}
              onClick={() => enviar.mutate(true)} className="border-state-warn text-state-warn">
              {t("finance.cartera.send")}
            </Button>
          ) : (
            <Button data-testid="cartera-send-button" disabled={enviar.isPending || !previews.length}
              onClick={() => enviar.mutate(false)} className="bg-ink text-paper hover:opacity-90">
              {t("finance.cartera.send")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
